// @system — Article body. Renders the server-rendered `content_html` (figures, tables, code,
// headings with ids) and falls back to the minimal markdown renderer when a post has no HTML
// (bundled seed posts, rows written before the renderer existed).
//
// Sanitization: `content_html` is produced ONLY by the server's renderer
// (lib/@system/Blog/markdown.js → marked + sanitize-html with an allow-list) at write time — both
// for the editor and for `POST /api/blog/posts` pushes, whose `content_html` is sanitized before
// it is stored. The client has no HTML sanitizer in its dependency tree (no DOMPurify /
// sanitize-html — adding one would grow the public bundle for a second pass over already-clean
// HTML), so this component trusts `content_html` by contract and injects it via
// dangerouslySetInnerHTML. If a client-side sanitizer ever lands in client/package.json, run it in
// `prepareHtml()` below; nothing else needs to change.
import { useMemo } from 'react'
import { ScrollRail } from '@/app/components/@system/ScrollRail'
import { MarkdownContent } from './MarkdownContent'
import { ensureHeadingIds, splitArticle } from './article-helpers'

/**
 * Typography for the raw HTML — brand tokens only, scoped with `[&_tag]` variants so the classes
 * apply to every element of the injected markup (no @tailwindcss/typography in this template).
 */
const ARTICLE_CLASSES = [
  'max-w-none text-brand-text',
  '[&_h2]:mt-8 [&_h2]:mb-3 [&_h2]:scroll-mt-24 [&_h2]:text-xl [&_h2]:font-bold',
  '[&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_h4]:mt-4 [&_h4]:mb-2 [&_h4]:text-sm [&_h4]:font-semibold',
  '[&_p]:my-3 [&_p]:text-sm [&_p]:leading-relaxed',
  '[&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:my-1 [&_li]:text-sm',
  '[&_a]:text-brand-primary [&_a]:underline [&_a]:underline-offset-4 [&_a:hover]:opacity-80',
  '[&_strong]:font-semibold',
  '[&_blockquote]:my-4 [&_blockquote]:border-l-4 [&_blockquote]:border-brand-primary/40 [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:text-brand-text-muted',
  '[&_code]:rounded [&_code]:bg-brand-surface [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-xs',
  '[&_pre]:my-0 [&_pre]:rounded-lg [&_pre]:border [&_pre]:bg-brand-surface [&_pre]:p-4 [&_pre]:text-sm [&_pre]:font-mono [&_pre_code]:bg-transparent [&_pre_code]:p-0',
  '[&_figure]:my-6 [&_figure]:mx-0 [&_figcaption]:mt-2 [&_figcaption]:text-center [&_figcaption]:text-xs [&_figcaption]:text-brand-text-muted',
  '[&_img]:h-auto [&_img]:max-w-full [&_img]:rounded-lg [&_img]:border',
  '[&_table]:w-full [&_table]:text-sm [&_th]:border-b [&_th]:px-3 [&_th]:py-2 [&_th]:text-left [&_th]:font-semibold [&_td]:border-b [&_td]:px-3 [&_td]:py-2 [&_td]:align-top',
  '[&_hr]:my-8 [&_hr]:border-brand-border',
].join(' ')

/**
 * Normalise the server HTML for the page: heading ids for the TOC anchors. The sanitizer pass
 * would live here too (see the header comment).
 * @param {string} html
 */
function prepareHtml(html) {
  return ensureHeadingIds(html)
}

/**
 * @typedef {object} ArticleBodyProps
 * @property {import('../BlogPage/helpers').BlogPost} post
 */

/** @param {ArticleBodyProps} props */
export function ArticleBody({ post }) {
  const segments = useMemo(
    () => (post.contentHtml ? splitArticle(prepareHtml(post.contentHtml)) : null),
    [post.contentHtml],
  )

  if (!segments) return <MarkdownContent raw={post.content} />

  return (
    <div className={ARTICLE_CLASSES} data-testid="article-html">
      {segments.map((seg, i) =>
        seg.kind === 'rail' ? (
          // Wide tables / code scroll inside a keyboard-reachable rail (docs/ACCESSIBILITY.md §3).
          <ScrollRail key={i} className="my-4" dangerouslySetInnerHTML={{ __html: seg.html }} />
        ) : (
          <div key={i} dangerouslySetInnerHTML={{ __html: seg.html }} />
        ),
      )}
    </div>
  )
}
